export interface PaymentSummary {
  payment_mode: 'pg' | 'bank' | 'combined';
  pg_amount: number;
  bank_amount: number;
  pg_paid: boolean;
  bank_paid: boolean;
  pg_due_amount: number;
  bank_due_amount: number;
  pg_paid_at: string | null;
  bank_paid_at: string | null;
  pg_deadline?: string | null;
  pg_attention?: boolean;
  pg_overdue?: boolean;
  pg_auto_cancel?: boolean;
  deposit_deadline: string | null;
  deposit_attention: boolean;
  deposit_overdue: boolean;
  bank_auto_cancel?: boolean;
  bank_account: { bank_name: string; bank_num: string; depositor: string } | null;
}

export function validPaymentSummary(value: any): value is PaymentSummary {
  return !!value && ['pg', 'bank', 'combined'].includes(value.payment_mode)
    && [value.pg_amount, value.bank_amount, value.pg_due_amount, value.bank_due_amount].every(amount => Number.isSafeInteger(amount) && amount >= 0)
    && [value.pg_paid, value.bank_paid, value.deposit_attention, value.deposit_overdue].every(flag => typeof flag === 'boolean')
    && [value.pg_attention, value.pg_overdue, value.pg_auto_cancel, value.bank_auto_cancel].every(flag => flag === undefined || typeof flag === 'boolean')
    && (value.pg_deadline == null || (typeof value.pg_deadline === 'string' && Number.isFinite(Date.parse(value.pg_deadline))));
}

export const paymentModeLabel = (mode: PaymentSummary['payment_mode']) =>
  mode === 'combined' ? '복합 결제' : mode === 'bank' ? '무통장 입금' : 'PG 결제';

export const paymentDate = (value: string | null) => {
  if (!value || Number.isNaN(Date.parse(value))) return '미설정';
  return new Date(value).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function isStatementPaid(data: { pay_status?: string | null; payment_summary?: PaymentSummary }) {
  if (data.payment_summary) return data.payment_summary.pg_due_amount === 0 && data.payment_summary.bank_due_amount === 0;
  return ["결제완료", "부분취소", "전체취소", "결제취소"].includes(data.pay_status || "");
}

export function isPgPaid(data: { pay_status?: string | null; payment_summary?: PaymentSummary }) {
  return data.payment_summary ? data.payment_summary.pg_due_amount === 0 : isStatementPaid(data);
}

export type PaymentMethod = 'card' | 'HF';

export const BUYER_PAYMENT_LOGIN_MESSAGE = "현재 로그인한 계정은 이 주문의 구매자 계정이 아닙니다. 주문한 구매자 계정으로 로그인한 뒤 정산서를 다시 열어주세요.";

export function paymentRequest(data: any, method: PaymentMethod = 'card') {
  if (data?.is_order_owner === false) throw new Error(BUYER_PAYMENT_LOGIN_MESSAGE);
  if (data?.payment_summary?.pg_auto_cancel && data.payment_summary.pg_overdue) throw new Error('결제 기한이 지나 자동 취소 처리 중입니다. 주문 상태를 새로고침해주세요.');
  if (!['card', 'HF'].includes(method)) throw new Error("지원하지 않는 결제수단입니다.");
  if (data?.payment_result === 'review') throw new Error("이전 결제 결과를 확인 중입니다. 고객센터에 문의해주세요.");
  if (!data || isPgPaid(data) || !data.details?.some((item: any) => item.order_status === "결제대기")) {
    throw new Error("결제 가능한 주문이 없습니다.");
  }
  const total = Number(data.payment_summary?.pg_due_amount ?? data.totalPayment);
  const delivery = data.payment_summary ? 0 : Number(data.delivery_price);
  const ids = [data.order_seq, data.live_seq, data.store_seq].map(Number);
  if (!ids.every(n => Number.isSafeInteger(n) && n > 0) || !Number.isSafeInteger(total) || total <= 0 || !Number.isSafeInteger(delivery) || delivery < 0 || delivery > total) {
    throw new Error("주문 금액과 정보를 다시 확인해주세요.");
  }
  return { orderSeq: ids[0], liveSeq: ids[1], storeSeq: ids[2], total_payment: total - delivery, delivery_price: delivery, method };
}
